import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import Header from './Header';
import Footer from './Footer';
import Pagination from './Pagination';
import NativeAdSection from './NativeAdSection';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Video Card Component
const VideoCard = ({ video, onVideoClick }) => {
  return (
    <Card 
      className="video-card border-orange-500/10 cursor-pointer group overflow-hidden"
      onClick={() => onVideoClick(video.id)}
      data-testid={`video-card-${video.id}`}
    >
      <div className="relative aspect-video">
        <img
          src={video.thumbnail_url || `https://picsum.photos/400/225?random=${video.id}`}
          alt={video.title}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
        />
        <div className="absolute bottom-2 right-2 bg-black/80 px-2 py-1 rounded text-xs text-white backdrop-blur-sm">
          {video.duration ? `${Math.floor(video.duration / 60)}:${(video.duration % 60).toString().padStart(2, '0')}` : '10:30'}
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300">
          <div className="play-button-overlay w-16 h-16 rounded-full flex items-center justify-center animate-bounce">
            <div className="w-0 h-0 border-l-[20px] border-l-white border-y-[12px] border-y-transparent ml-1" />
          </div>
        </div>
        {/* HD Badge removed as requested */}
      </div>
      <CardContent className="p-4">
        <h3 className="text-white font-semibold mb-2 line-clamp-2 group-hover:text-orange-400 transition-colors">
          {video.title}
        </h3>
        <div className="flex items-center justify-between text-sm text-gray-400">
          <span>{video.view_count.toLocaleString()} views</span>
          <div className="flex items-center space-x-1">
            <span className="text-orange-400">⭐</span>
            <span>{video.rating}</span>
          </div>
        </div>
        {video.tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {video.tags.slice(0, 3).map((tag, index) => (
              <Badge key={index} variant="secondary" className="text-xs bg-orange-600/20 text-orange-300 border-orange-500/30">
                {tag}
              </Badge>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Category Card Component
const CategoryCard = ({ category, onCategoryClick }) => {
  return (
    <Card 
      className="video-card border-orange-500/10 cursor-pointer group overflow-hidden"
      onClick={() => onCategoryClick(category.id)}
      data-testid={`category-card-${category.id}`}
    >
      <div className="relative aspect-video">
        <img
          src={category.image_url || `https://picsum.photos/400/225?random=cat-${category.id}`}
          alt={category.name}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-4">
          <h3 className="text-white font-bold text-lg mb-1">
            {category.name}
          </h3>
          <p className="text-gray-300 text-sm opacity-90">
            {category.video_count || 0} videos
          </p>
        </div>
        <div className="absolute top-2 right-2">
          <Badge className="bg-orange-600/90 text-white text-xs">
            Category
          </Badge>
        </div>
      </div>
    </Card>
  );
};

const CategoryPage = () => {
  const { categoryId } = useParams();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [videos, setVideos] = useState([]);
  const [currentCategory, setCurrentCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const videosPerPage = 20;

  useEffect(() => {
    if (categoryId) {
      fetchCategoryVideos();
    } else {
      fetchCategories();
    }
  }, [categoryId, currentPage]);

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategoryVideos = async () => {
    setLoading(true);
    try {
      // Fetch category info
      const categoryResponse = await axios.get(`${API}/categories/${categoryId}`);
      setCurrentCategory(categoryResponse.data);

      // Fetch videos for this category
      const videosResponse = await axios.get(`${API}/videos`, {
        params: {
          category_id: categoryId,
          page: currentPage,
          limit: videosPerPage
        }
      });

      // Handle paginated response format {items: [...], total: X}
      const responseData = videosResponse.data;
      if (responseData.items) {
        setVideos(responseData.items);
        setTotalPages(Math.ceil(responseData.total / videosPerPage));
      } else {
        // Fallback for non-paginated response
        setVideos(Array.isArray(responseData) ? responseData : []);
        setTotalPages(1);
      }
      
    } catch (error) {
      console.error('Failed to fetch category videos:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryClick = (categoryId) => {
    navigate(`/categories/${categoryId}`);
  };

  const handleVideoClick = (videoId) => {
    navigate(`/video/${videoId}`);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleBackToCategories = () => {
    navigate('/categories');
  };

  const handleBackToHome = () => {
    navigate('/');
  };

  return (
    <div className="min-h-screen" data-testid="category-page">
      {/* Header with sticky functionality */}
      <Header showNavigation={true} />
      
      {/* Breadcrumb Navigation */}
      <div className="bg-gray-900/50">
        <div className="container mx-auto px-6 py-3">
          <div className="flex items-center space-x-4">
            <Button
              onClick={handleBackToHome}
              variant="ghost"
              className="text-gray-300 hover:text-orange-400 hover:bg-orange-600/10 text-sm"
              data-testid="back-home-btn"
            >
              ← Home
            </Button>
            
            {categoryId && (
              <Button
                onClick={handleBackToCategories}
                variant="ghost"
                className="text-gray-300 hover:text-orange-400 hover:bg-orange-600/10 text-sm"
                data-testid="back-categories-btn"
              >
                ← All Categories
              </Button>
            )}
          </div>
        </div>
      </div>

      <main className="container mx-auto px-6 pb-8">
        {/* Page Title */}
        <div className="mb-8 pt-12">
          <h1 className="text-4xl font-bold title-primary mb-4">
            {categoryId ? (
              currentCategory ? currentCategory.name : 'Loading...'
            ) : (
              'Browse Categories'
            )}
          </h1>
          <p className="text-gray-400">
            {categoryId ? (
              'Explore premium videos in this category'
            ) : (
              'Discover our extensive collection of premium content categories'
            )}
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-20">
            <div className="text-white text-xl animate-pulse">Loading...</div>
          </div>
        ) : (
          <>
            {!categoryId ? (
              /* Categories Grid */
              <div className="animate-slideInUp">
                {categories.length > 0 ? (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
                    {categories.map((category) => (
                      <CategoryCard 
                        key={category.id} 
                        category={category} 
                        onCategoryClick={handleCategoryClick} 
                      />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-20 animate-fadeIn">
                    <div className="text-6xl mb-4">📂</div>
                    <div className="text-xl text-gray-400 mb-2">No categories available</div>
                    <div className="text-gray-500">Categories will appear here once they're created</div>
                  </div>
                )}
              </div>
            ) : (
              /* Videos Grid */
              <div className="animate-slideInUp">
                {videos.length > 0 ? (
                  <>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
                      {videos.map((video) => (
                        <VideoCard 
                          key={video.id} 
                          video={video} 
                          onVideoClick={handleVideoClick} 
                        />
                      ))}
                    </div>
                    
                    {/* Pagination */}
                    <Pagination
                      currentPage={currentPage}
                      totalPages={totalPages}
                      onPageChange={handlePageChange}
                    />
                  </>
                ) : (
                  <div className="text-center py-20 animate-fadeIn">
                    <div className="text-6xl mb-4">🎬</div>
                    <div className="text-xl text-gray-400 mb-2">No videos in this category</div>
                    <div className="text-gray-500">Videos will appear here once they're added to this category</div>
                  </div>
                )}
              </div>
            )}
          </>
        )}
        
        {/* Native Ad Section */}
        <NativeAdSection />
      </main>

      <Footer />
    </div>
  );
};

export default CategoryPage;